import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { domainTone, overrideDetail, statusPillLabel } from '../../../../../../shared/component/status-pill'
import { DomainPill } from '../../../../../../shared/sso-workspace/pill'
import { UnbindDialog } from '../../../../../../shared/sso-workspace/unbind-dialog'
import { useApiFormProviderDomain, useApiListProviderDomain } from '../../../../../../api-new/state/auth'
import { ErrorReference } from '../../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

const serverMessage = (exp, fallback) => (exp && exp.data && exp.data.message) || fallback

// The connection's email domains, and binding them (SSO journey audit B1):
// nothing in the admin could bind or unbind a domain to a connection since
// the old form's domain list went. "Bind a domain…" offers the domains of
// the organizations this connection belongs to (its owner and every linked
// organization); each bound domain can be unbound, with the
// last-domain-of-an-active-connection warning and "Unbind and deactivate".
// Both go through the sys /provider-domain bindings routes.
//
// Platform-held domains (no owning organization) are offered only on a
// platform-held connection (owner 2026-11-03): on one an organization owns,
// binding, say, medtronic.com would send Medtronic's people to that
// organization's sign-in. ownerOrganizationID is the connection's owner
// (providers.organization_id): null when it is platform-held, undefined
// while the workspace hasn't loaded it, when the picker waits rather than
// guess. The sys bind route itself accepts any domain on any connection.
// onAnnounce is the section's one polite status line (a second live
// region would double up with Organizations people join's).
export const WorkspaceDomains = ({ connectionName, providerID, domains, providerActive, isPublic, ownerOrganizationID, organizationIDs, canEdit, onDomainsChanged, onProviderChanged, onAnnounce = () => {} }) => {
  const domainForm = useApiFormProviderDomain()
  const allDomains = useApiListProviderDomain()

  const [picking, setPicking] = useState(false)
  // Every domain, as the picker last loaded them: undefined while loading,
  // null if they failed.
  const [rows, setRows] = useState(undefined)
  const [selected, setSelected] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [unbinding, setUnbinding] = useState(null)
  const [unbindError, unbindFailure, setUnbindError] = useErrorLine()

  const boundIDs = new Set((domains || []).map(d => d.id))
  const allowedOwners = new Set([ownerOrganizationID, ...(organizationIDs || [])].filter(Boolean))
  const ownerKnown = ownerOrganizationID !== undefined
  const owned = ownerOrganizationID != null
  const offered = d => !boundIDs.has(d.id) && (d.organizationID == null ? !owned : allowedOwners.has(d.organizationID))
  // The picker's list: undefined while the domains or the owner load.
  const candidates = rows === null ? null : rows === undefined || !ownerKnown ? undefined : rows.filter(offered)

  const openPicker = async () => {
    setPicking(true)
    setError(null)
    setSelected('')
    setRows(undefined)
    try {
      setRows((await allDomains.get()) || [])
    } catch (exp) {
      setRows(null)
    }
  }

  const handleBind = async () => {
    const domain = (candidates || []).find(d => String(d.id) === selected)
    if (!domain) return
    setBusy(true)
    setError(null)
    try {
      await domainForm.postArg(`/${domain.id}/bindings`, { providerID: Number(providerID) })
    } catch (exp) {
      setError(serverMessage(exp, `Could not bind ${domain.domain}.`), exp)
      setBusy(false)
      return
    }
    setBusy(false)
    setPicking(false)
    onAnnounce(`Bound ${domain.domain}.`)
    onDomainsChanged && (await onDomainsChanged())
    // Binding a routable domain can take a waiting connection live.
    onProviderChanged && (await onProviderChanged())
  }

  const handleUnbind = async deactivate => {
    const domain = unbinding
    setBusy(true)
    setUnbindError(null)
    try {
      await domainForm.delete(`${domain.id}/bindings/${providerID}${deactivate ? '?deactivate=true' : ''}`)
    } catch (exp) {
      setUnbindError(serverMessage(exp, `Could not unbind ${domain.domain}.`), exp)
      setBusy(false)
      return
    }
    setBusy(false)
    setUnbinding(null)
    onAnnounce(deactivate ? `Unbound ${domain.domain} and deactivated the connection.` : `Unbound ${domain.domain}.`)
    onDomainsChanged && (await onDomainsChanged())
    if (deactivate && onProviderChanged) await onProviderChanged()
  }

  const ownerName = d => d.organizationName || 'EducationXR (platform)'

  return (
    <>
      <div className="sw-subh">
        <h3>Email domains</h3>
        <div className="d-flex gap-2">
          {canEdit && <button type="button" className="btn btn-sm sw-btn" aria-expanded={picking} onClick={() => (picking ? setPicking(false) : openPicker())}>Bind a domain…</button>}
          <Link className="btn btn-sm sw-btn" to="/admin/sso/domains">Manage domains</Link>
        </div>
      </div>

      {picking &&
        <div className="sw-inline-panel" id="sw-bind-panel">
          <div className="sw-field">
            <label className="sw-label" htmlFor="sw-bind-domain">Domain to bind</label>
            {candidates === undefined && <p className="sw-hint m-0">Loading domains…</p>}
            {candidates === null && <p className="sw-hint m-0">Domains couldn't be loaded.</p>}
            {candidates && candidates.length === 0 &&
              <p className="sw-hint m-0">
                {owned
                  ? "This connection's organizations have no domains left to bind. Add the domain to the organization first, on its SSO tab or on the Domains page."
                  : "No domains to bind. Add the organization's domain on its SSO tab or on the Domains page first."}
              </p>}
            {candidates && candidates.length > 0 &&
              <select id="sw-bind-domain" className="form-select" value={selected} onChange={e => setSelected(e.target.value)}>
                <option value="">Choose a domain</option>
                {candidates.map(d =>
                  <option key={d.id} value={d.id}>{d.domain} · {ownerName(d)} · {statusPillLabel(domainTone(d))}</option>)}
              </select>}
            <div className="sw-hint">
              {owned ? "Domains of this connection's organizations." : "Domains of this connection's organizations, or held by EducationXR."} People are sent here once the domain is verified or overridden.
            </div>
          </div>
          {error && <p className="text-danger small m-0" role="alert">{error}</p>}
          {error && <ErrorReference error={failure} className="mt-0" />}
          <div className="sw-inline-actions">
            <button type="button" className="btn sw-btn" onClick={() => setPicking(false)}>Cancel</button>
            <button type="button" className="btn sw-btn-primary" disabled={!selected || busy} onClick={handleBind}>Bind</button>
          </div>
        </div>}

      {domains === undefined && <p className="sw-hint m-0">Loading domains…</p>}
      {domains === null && <p className="sw-hint m-0">Domains couldn't be loaded.</p>}
      {domains && domains.length === 0 &&
        <p className="sw-hint m-0">
          {isPublic
            ? "No domains. It's a public sign-in method, offered to everyone on the sign-in page."
            : "No domains yet, so nobody is sent to this connection. Bind the organization's email domain to send its people here."}
        </p>}
      {domains && domains.map(d =>
        <div key={d.id} className="sw-domain">
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <code>{d.domain}</code>
            <DomainPill tone={domainTone(d)} title={overrideDetail(d) || undefined} />
            {overrideDetail(d) && <span className="sw-hint">{overrideDetail(d)}</span>}
          </span>
          {canEdit &&
            <button type="button" className="btn btn-sm sw-btn-outline-danger" onClick={() => { setUnbindError(null); setUnbinding(d) }}>
              Unbind<span className="visually-hidden"> {d.domain}</span>
            </button>}
        </div>)}

      <UnbindDialog
        open={Boolean(unbinding)}
        domain={unbinding ? unbinding.domain : ''}
        connectionName={connectionName || 'this connection'}
        lastOfActive={Boolean(unbinding && providerActive && !isPublic && (domains || []).length === 1)}
        busy={busy}
        error={unbindError}
        failure={unbindFailure}
        onUnbind={() => handleUnbind(false)}
        onUnbindAndDeactivate={() => handleUnbind(true)}
        onCancel={() => setUnbinding(null)}
      />
    </>
  )
}
